import React, { useState, useEffect } from 'react';
import { TrendingUp, Save, Loader2, ExternalLink, Check } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const AnalyticsTrackingTab = ({ isDark, authFetch, showToast }) => {
  const [settings, setSettings] = useState({
    ga_measurement_id: '',
    fb_pixel_id: '',
    custom_head_scripts: ''
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/tracking-settings`);
        if (res.ok) {
          const data = await res.json();
          setSettings({
            ga_measurement_id: data.ga_measurement_id || '',
            fb_pixel_id: data.fb_pixel_id || '',
            custom_head_scripts: data.custom_head_scripts || ''
          });
        }
      } catch (e) {
        console.error('Failed to load tracking settings');
      }
      setLoaded(true);
    };
    fetchSettings();
  }, [authFetch]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/tracking-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        showToast('Tracking settings saved! Changes will take effect on next page load.');
      } else {
        showToast('Failed to save settings', 'error');
      }
    } catch (e) {
      showToast('Error saving settings', 'error');
    }
    setSaving(false);
  };

  const inputStyle = {
    backgroundColor: isDark ? '#1a1a1a' : '#fff',
    color: isDark ? '#fff' : '#000',
    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-6 h-6" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
          <div>
            <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Analytics & Tracking</h2>
            <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Manage tracking codes for your website</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2"
          style={{ background: isDark ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)', color: '#fff' }}
          data-testid="save-tracking-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Google Analytics */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: isDark ? 'rgba(251,146,60,0.2)' : 'rgba(251,146,60,0.1)' }}>
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill={isDark ? '#fb923c' : '#ea580c'}>
              <path d="M22.84 2.998c-.642-.372-1.478-.372-2.12 0l-6.013 3.47c-.64.37-1.063 1.058-1.063 1.802v6.94c0 .744.423 1.432 1.063 1.802l6.013 3.47c.642.372 1.478.372 2.12 0l.157-.091V2.998zm-11.7 6.754v6.94c0 .744.423 1.432 1.063 1.802l.72.416V8.272l-1.22.698c-.341.198-.563.578-.563.782zM7.14 12.69v2.058c0 .744.423 1.432 1.063 1.802l.72.416v-4.974l-.72.416c-.64.37-1.063 1.058-1.063 1.282z"/>
            </svg>
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>Google Analytics</h3>
            <p className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>
              Track website visitors, page views, and user behavior
            </p>
          </div>
          {settings.ga_measurement_id && (
            <span className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: isDark ? 'rgba(0,255,136,0.15)' : 'rgba(16,185,129,0.15)', color: isDark ? '#00ff88' : '#10b981' }}>
              <Check className="w-3 h-3" /> Active
            </span>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
            Measurement ID
          </label>
          <input
            type="text"
            placeholder="G-XXXXXXXXXX"
            value={settings.ga_measurement_id}
            onChange={(e) => setSettings(prev => ({ ...prev, ga_measurement_id: e.target.value }))}
            className="w-full p-3 rounded-lg text-sm font-mono"
            style={inputStyle}
            data-testid="ga-measurement-id"
          />
          <p className="text-xs mt-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
            Find this in Google Analytics &rarr; Admin &rarr; Data Streams &rarr; Your stream
          </p>
        </div>
      </div>

      {/* Facebook Pixel */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)' }}>
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill={isDark ? '#60a5fa' : '#2563eb'}>
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>Facebook Pixel</h3>
            <p className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>
              Track conversions from Facebook & Instagram ads
            </p>
          </div>
          {settings.fb_pixel_id && (
            <span className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: isDark ? 'rgba(0,255,136,0.15)' : 'rgba(16,185,129,0.15)', color: isDark ? '#00ff88' : '#10b981' }}>
              <Check className="w-3 h-3" /> Active
            </span>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
            Pixel ID
          </label>
          <input
            type="text"
            placeholder="123456789012345"
            value={settings.fb_pixel_id}
            onChange={(e) => setSettings(prev => ({ ...prev, fb_pixel_id: e.target.value }))}
            className="w-full p-3 rounded-lg text-sm font-mono"
            style={inputStyle}
            data-testid="fb-pixel-id"
          />
          <p className="text-xs mt-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
            Find this in Facebook Events Manager &rarr; Settings
          </p>
        </div>
      </div>

      {/* Custom Scripts */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: isDark ? 'rgba(168,85,247,0.2)' : 'rgba(168,85,247,0.1)' }}>
            <span style={{ color: isDark ? '#c084fc' : '#7c3aed', fontWeight: 'bold', fontSize: '16px' }}>&lt;/&gt;</span>
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>Custom Scripts</h3>
            <p className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>
              Add any custom tracking scripts (TikTok Pixel, Hotjar, etc.)
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
            Custom &lt;script&gt; Tags
          </label>
          <textarea
            placeholder={'<script>\n  // Paste your custom tracking code here\n</script>'}
            value={settings.custom_head_scripts}
            onChange={(e) => setSettings(prev => ({ ...prev, custom_head_scripts: e.target.value }))}
            rows={6}
            className="w-full p-3 rounded-lg text-sm font-mono"
            style={inputStyle}
            data-testid="custom-scripts"
          />
          <p className="text-xs mt-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
            These scripts will be injected into the &lt;head&gt; of every page
          </p>
        </div>
      </div>

      {/* Info box */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(0,255,255,0.08)' : 'rgba(14,165,233,0.08)', border: `1px solid ${isDark ? '#00ffff' : '#0ea5e9'}` }}>
        <p className="text-sm" style={{ color: isDark ? '#00ffff' : '#0369a1' }}>
          <strong>How it works:</strong> Tracking codes are loaded dynamically on every page of your website. After saving, reload your site to activate. Changes take effect immediately — no redeployment needed for code updates.
        </p>
      </div>
    </div>
  );
};

export default AnalyticsTrackingTab;
